import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'

import * as state from '../../../../../../../state'

import * as components from './components'

/**
 * Shell for a single space report's details page.
 *
 * Owns the page header (breadcrumbs, pagination, download) and delegates the
 * body to a content component chosen by report id.
 */
@ngCore.Component({
  imports: [
    ds.button.Component,
    ds.illustration.emptyState.Component,
    ds.layout.page.Component,
    ds.layout.page.child.header.Component,
    ds.layout.page.child.header.child.Pagination.Component,
    ds.layout.widget.Component,
    components.AccountList.Component,
    components.DataExport.Component,
    components.EmployeeList.Component,
    components.RoomLinks.Component,
    components.RoomSupportingInformation.Component,
    components.RoomsByAccount.Component,
    components.RoomsByOccupant.Component,
    components.RoomsByResponsiblePerson.Component,
    components.SpaceSalaryComparison.Component,
    components.UnassignedSponsoredAccounts.Component,
    components.UnassignedSponsoredPeople.Component,
    components.ProgressByOrganization.Component,
    components.UnclaimedRooms.Component,
  ],
  selector: 'atn-space-report',
  standalone: true,
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
export class Component {
  /** @see {@link ds.color.Legacy.PredefinedStyle} */
  public readonly PredefinedStyleEnum = ds.color.Legacy.PredefinedStyle

  /** Exposes {@link ents.extra.space.Report.Id} to the template. */
  public readonly ReportId = ents.extra.space.Report.Id

  /** @see {@link state.me.Feature} */
  private readonly _meFeature = ngCore.inject(state.me.Feature)
  /** @see {@link state.space.reports.Collection} */
  private readonly _reportsEc = ngCore.inject(state.space.reports.Collection)

  /** @see {@link state.space.reports.plugins.download.Plugin} */
  public readonly downloadPlugin = this._reportsEc.plugins
    .lookup(state.space.reports.plugins.download.Plugin)
    .get()

  /** Currently selected report. */
  public readonly reportSg =
    this._reportsEc.plugins.currentEntityPlugin.feature.selectors.getEntity()
      .asSignal

  /** Breadcrumbs for the page header. */
  public readonly breadcrumbsSg: ngCore.Signal<
    ds.layout.page.child.header.models.Breadcrumb[]
  > = ngCore.computed(() => {
    const report = this.reportSg()
    if (!report) {
      return []
    }
    return [
      {
        name: 'Reports',
        url: this._meFeature.contexts.selectors
          .getUrlForLink({
            goto: { navigationId: ents.extra.prebuilt.Page.Id.SpaceReports },
          })
          .asSignal(),
      },
      { name: report.name },
    ]
  })

  /**
   * Total count of reports, used by the page header pagination.
   *
   * @default 0
   */
  public readonly numberOfPagesSg = ngCore.computed(
    (
      totalCount = this._reportsEc.plugins.currentListPlugin.feature.selectors.matchedCount.asSignal(),
    ) => totalCount ?? 0,
  )

  /**
   * By default, this will be undefined if the selected report is not found in
   * the list. If it is found, it returns the selected index incremented by
   * one, as the selected pages begin counting from 1 rather than 0.
   */
  public readonly reportIdxSg =
    this._reportsEc.plugins.currentEntityPlugin.feature.selectors.getPosition()
      .asSignal

  /** Handles clicking on the previous button in the page header pagination. */
  public previousPageClickHandler(): void {
    const source = 'ReportDetails.Page.Pagination.PreviousButton'
    const previousId =
      this._reportsEc.plugins.currentEntityPlugin.feature.selectors
        .getPreviousId()
        .asSignal()

    if (previousId !== undefined) {
      this._reportsEc.plugins.currentEntityPlugin.storage.actions.putInIdLookup
        .from(source)
        .dispatch({ value: previousId })
    }
  }

  /** Handles clicking on the next button in the page header pagination. */
  public nextPageClickHandler(): void {
    const source = 'ReportDetails.Page.Pagination.NextButton'
    const nextId = this._reportsEc.plugins.currentEntityPlugin.feature.selectors
      .getNextId()
      .asSignal()

    if (nextId !== undefined) {
      this._reportsEc.plugins.currentEntityPlugin.storage.actions.putInIdLookup
        .from(source)
        .dispatch({ value: nextId })
    }
  }

  /** Handles clicking the download button, downloading the current report. */
  public downloadClickHandler(): void {
    this.downloadPlugin.feature.actions.download
      .from('SpaceReportDetails.Download.Button.Clicked')
      .dispatch()
  }
}
